import type {
  ArrayItemStatus,
  FlatDiffRow,
} from '@seen/core/queued-edits/model'
import { cn } from '@seen/ui'
import type { JSX } from 'react'

// `showOriginal` adds a fourth column carrying each row's value at the pinned
// base version — only meaningful for a drifted entry, where Before is the
// CURRENT value rather than the one the proposer drafted against.
export function DiffTable({
  rows,
  showTechnical,
  showOriginal,
}: {
  rows: FlatDiffRow[]
  showTechnical: boolean
  showOriginal: boolean
}): JSX.Element {
  return (
    <table className="w-full table-fixed border-collapse text-sm">
      {/* table-fixed + column widths so long unbroken values (URLs, refs) wrap
          inside their cell instead of forcing the table wider than the card. */}
      <colgroup>
        {showOriginal ? (
          <>
            <col className="w-[31%]" />
            <col className="w-[23%]" />
            <col className="w-[23%]" />
            <col className="w-[23%]" />
          </>
        ) : (
          <>
            <col className="w-[34%]" />
            <col className="w-[33%]" />
            <col className="w-[33%]" />
          </>
        )}
      </colgroup>
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="px-4 py-1.5 font-medium">Field</th>
          {showOriginal && (
            <th
              className="px-2 py-1.5 font-medium"
              title="Value when this change was proposed"
            >
              Original
            </th>
          )}
          <th className="px-2 py-1.5 font-medium">Before</th>
          <th className="px-2 py-1.5 font-medium">After</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.key}
            className={cn(
              'border-border/60 border-t align-top',
              row.status === 'unchanged' && 'text-muted-foreground'
            )}
          >
            <td className="px-4 py-1.5 wrap-break-word">
              <div className="flex items-start gap-1.5">
                <StatusIcon status={row.status} />
                <span className="min-w-0">{row.label}</span>
              </div>
              {showTechnical && row.fhirPath && (
                <div className="mt-0.5 font-mono text-2xs break-all text-muted-foreground/70">
                  {row.fhirPath}
                </div>
              )}
              {showTechnical && row.typeChips && row.typeChips.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {row.typeChips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded bg-muted px-1 py-0.5 text-2xs text-muted-foreground"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </td>
            {showOriginal && (
              <td className="px-2 py-1.5 wrap-break-word">
                <DiffValue
                  value={row.original}
                  tone="original"
                  status={row.status}
                />
              </td>
            )}
            <td className="px-2 py-1.5 wrap-break-word">
              <DiffValue value={row.before} tone="before" status={row.status} />
              {showTechnical && row.code && (
                <div className="mt-0.5 font-mono text-2xs break-all text-muted-foreground/70">
                  code: {row.code}
                </div>
              )}
            </td>
            <td className="px-2 py-1.5 wrap-break-word">
              <DiffValue value={row.after} tone="after" status={row.status} />
              {showTechnical && row.system && (
                <div className="mt-0.5 font-mono text-2xs break-all text-muted-foreground/70">
                  system: {row.system}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DiffValue({
  value,
  tone,
  status,
}: {
  value: string | undefined
  tone: 'original' | 'before' | 'after'
  status: ArrayItemStatus
}): JSX.Element {
  if (value === undefined) {
    return <span className="text-muted-foreground/40">—</span>
  }
  return (
    <span
      className={cn(
        // The base-version value is context, not part of the change being
        // decided — it stays neutral so it never competes with before/after.
        tone === 'original' && 'text-muted-foreground',
        tone === 'before' &&
          status === 'removed' &&
          'text-red-600 line-through',
        tone === 'after' && status === 'added' && 'font-medium text-pine-700',
        tone === 'after' && status === 'modified' && 'font-medium'
      )}
    >
      {value}
    </span>
  )
}

// + create · ~ update · - delete · (blank) unchanged
function StatusIcon({ status }: { status: ArrayItemStatus }): JSX.Element {
  const glyph = { added: '+', modified: '~', removed: '-', unchanged: '' }[
    status
  ]
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block w-3 text-center font-mono font-semibold leading-none',
        status === 'added' && 'text-pine-600',
        status === 'modified' && 'text-amber-500',
        status === 'removed' && 'text-red-500'
      )}
    >
      {glyph}
    </span>
  )
}
